import { useEffect, useRef } from "react";
import { ensureTurnstileScript, turnstileSiteKey } from "../lib/captcha.js";

interface Props {
  /** Called with a token when the challenge passes, or null when it expires / errors. */
  onVerify: (token: string | null) => void;
  /** Turnstile "action" label, surfaced in the Cloudflare dashboard analytics. */
  action?: string;
}

/**
 * Visible Cloudflare Turnstile widget (Managed mode) for the sign-up form.
 *
 * Unlike the invisible getCaptchaToken() path used by the feedback forms, this
 * renders the checkbox widget inline so the user can see the bot check happen
 * before submitting — sign-up is where a bot getting through means a junk
 * verification email and SES-reputation damage, so the friction is warranted.
 *
 * Renders nothing when VITE_TURNSTILE_SITE_KEY is unset (local dev); callers
 * should treat "no widget" as "no token required" via isTurnstileConfigured().
 */
export function TurnstileWidget({ onVerify, action }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const siteKey = turnstileSiteKey();

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;

    ensureTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          size: "flexible",
          theme: "auto",
          retry: "auto",
          callback: (token) => onVerify(token),
          "expired-callback": () => onVerify(null),
          "error-callback": () => onVerify(null),
        });
      })
      .catch(() => {
        if (!cancelled) onVerify(null);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* widget already gone */
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, action, onVerify]);

  if (!siteKey) return null;
  return <div ref={containerRef} className="flex justify-center" />;
}
